import axios, { AxiosResponse } from "axios";
import { Feature } from "ol";
import { EsriJSON } from "ol/format";
import { ISummaryParams } from "../Registration/SummarySection";
import { config, defaultOption, proxyUrl } from "./config";
import { AttachmentType, PrintType } from "./enums";
import { getMultiPolygonGeometry } from "./utils";

export interface IEsriFieldInfo {
    alias: string;
    domain: IEsriDomain;
    editable: boolean;
    name: string;
    nullable: boolean;
    type: string;
}

export interface IEsriDomain {
    type: string;
    name: string;
    description: string;
    codedValues: IEsriCodedValue[];
    mergePolicy: string;
}

export interface IEsriCodedValue {
    code: string | number;
    name: string;
}

export interface IObjectIDs {
    objectId: number;
    globalId: string;
}

export interface IAttachmentPath {
    type: AttachmentType;
    url: string;
}
export interface IAttachmentFile {
    type: AttachmentType;
    endpoint: string;
    resourceId: string;
}

export interface ISearchParams {
    f: string;
    where: string;
    outFields: string;
}

const esrijsonFormat = new EsriJSON();

const fieldInfoCache: Record<number, IEsriFieldInfo[]> = {};

export const fetchFieldInfo = async (): Promise<IEsriFieldInfo[]> => {
    const params = {
        f: "json",
        returnGeometry: true,
        where: "1=1",
        outFields: "*",
        outSR: "3059"
    };

    return axios
        .get(proxyUrl + "proxy/db08b72d75954eeab9e3d3b19b03163d/NIeTimberFlow/MapServer/3", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const info = response.data.fields;
            // addDefaultCodedValuesOption(info);
            fieldInfoCache[0] = info;
            return info;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};
export const getFieldInfo = (): IEsriFieldInfo[] => fieldInfoCache[0];

// const addDefaultCodedValuesOption = (info: IEsriFieldInfo[]) => {
//     config.forms.eventForm.fields.map((inputField) => {
//         const currentFieldInfo = info.find((item) => item.name === inputField.field);
//         if (currentFieldInfo && currentFieldInfo.domain && currentFieldInfo.domain.codedValues) {
//             currentFieldInfo.domain.codedValues.unshift(defaultOption);
//         }
//     });
// };

export const updateFeatures = async (features: Feature[], layerId?: string) => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("g-recaptcha-response", recaptchaToken);
    queryString.append(
        "features",
        "[" + features.map((feature) => esrijsonFormat.writeFeature(feature)).toString() + "]"
    );

    return axios
        .post(
            proxyUrl + "proxy/recaptcha-8aa8cde7a8a3486e887ae5303a285950/" + (layerId || 0) + "/updateFeatures",
            queryString
        )
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response.data.updateResults;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};

export const requestObjectIds = async (features: Feature[], layerId?: string): Promise<IObjectIDs[]> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("g-recaptcha-response", recaptchaToken);
    queryString.append(
        "features",
        "[" + features.map((feature) => esrijsonFormat.writeFeature(feature)).toString() + "]"
    );

    return axios
        .post(
            proxyUrl + "proxy/recaptcha-8aa8cde7a8a3486e887ae5303a285950/" + (layerId || 0) + "/addFeatures",
            queryString
        )
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const ids: IObjectIDs[] = response.data.addResults.map((res) => ({
                objectId: res.objectId,
                globalId: res.globalId
            }));
            return ids;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const uploadFile = async (file: File, id: string): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }
    // this is temporary solution to set mime-type in client side, for .kml, .kmz files
    // API team is working on changes to avoid this
    let newFile = file;
    if (!file.type) {
        const extension = file.name.split(".").pop().toLowerCase();
        const mimeType =
            extension === "kmz"
                ? "application/vnd.google-earth.kmz"
                : extension === "kml"
                ? "application/vnd.google-earth.kml+xml"
                : "";
        const blob = new Blob([file], { type: mimeType });
        newFile = new File([blob], file.name, { type: mimeType });
    }
    /////////////////////////////////////////////////////////////////////////////////

    const formData = new FormData();
    formData.append("file", newFile);
    formData.append("RelatedObjectId", id);
    formData.append("FieldName", "FILEID");
    formData.append("LayerName", config.upload.layerName);
    formData.append("RelatedTable", config.upload.relatedTable);
    formData.append("g-recaptcha-response", recaptchaToken);

    return axios
        .post(config.upload.url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const printPdf = async (data: ISummaryParams, printType: PrintType): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("g-recaptcha-response", recaptchaToken);
    formData.append("data", JSON.stringify(data));
    printType === PrintType.Path && formData.append("f", "json");

    return axios
        .post(
            config.print.url + config.print.endpoint,
            formData,
            printType === PrintType.File && {
                responseType: "blob",
                headers: {
                    Accept: "application/pdf"
                }
            }
        )
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const sendEmail = async (
    to: string,
    attachments: IAttachmentPath[] | IAttachmentFile[]
): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("To", to);
    formData.append("Subject", config.email.subject);
    formData.append("Body", config.email.body);
    formData.append("g-recaptcha-response", recaptchaToken);

    if (attachments && attachments.length) {
        attachments.map((a, i) => {
            a.type && formData.append("ExternalAttachments[" + i + "].type", a.type);
            a.url && formData.append("ExternalAttachments[" + i + "].url", a.url);
            a.endpoint && formData.append("ExternalAttachments[" + i + "].endpoint", a.endpoint);
            a.resourceId && formData.append("ExternalAttachments[" + i + "].resourceId", a.resourceId);
        });
    }

    return axios
        .post(config.email.url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const executeQuery = async (params: ISearchParams, url: string, layerId: string): Promise<Feature[]> => {
    return axios
        .get(url + "/" + layerId + "/query", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            return esrijsonFormat.readFeatures(response.data);
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};

const getCaptchaToken = (): Promise<string> => {
    return new Promise((resolve) => {
        //@ts-ignore
        window.grecaptcha.ready(() => {
            //@ts-ignore
            window.grecaptcha
                .execute(config.reCaptchaSiteKey, { action: "submit" })
                .then((token) => resolve(token))
                .catch(() => resolve(null));
        });
    });
};

export const getLVMForestry = async (features: Feature[]): Promise<AxiosResponse> => {
    const jsonData = {
        geometry: getMultiPolygonGeometry(features),
        GeometryType: "esriGeometryPolygon",
        fields: [
            "LVMFORESTRYCODE",
            "LVMFORESTRYNAME",
            "LVMDISTRICTCODE",
            "LVMDISTRICTNAME",
            "MANAGERNAME",
            "MANAGERPHONE",
            "MANAGEREMAIL"
        ]
    };

    return axios
        .post(config.geometryService.url + config.geometryService.endpoint, jsonData, {
            headers: { "Content-Type": "application/json" }
        })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const getIntersectedForestryCount = async (features: Feature[]): Promise<AxiosResponse> => {
    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("outFields", "*");
    queryString.append("geometryType", "esriGeometryPolygon");
    queryString.append("spatialRel", "esriSpatialRelIntersects");
    queryString.append("returnCountOnly", "true");
    queryString.append("geometry", JSON.stringify(getMultiPolygonGeometry(features)));

    return axios
        .post(proxyUrl + "proxy/13d020edd12a4c44894a782aea9527c2/3/query", queryString)
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const loadSourceFeatures = (url: string, ext: number[], source): void => {
    const params = {
        f: "json",
        returnGeometry: true,
        outFields: "*",
        outSR: "3059",
        geometry: `{xmin: ${ext[0]}, ymin:${ext[1]}, xmax:${ext[2]}, ymax:${ext[3]}, spatialReference:{wkid:3059}}`
    };

    axios
        .get(url + "/query", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const features = esrijsonFormat.readFeatures(response.data);

            features.length && source.addFeatures(features);
        })
        .catch((err) => console.error(err));
};

export const identify = async (url: string, params): Promise<Feature[]> => {
    return axios
        .get(url + "/identify", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return esrijsonFormat.readFeatures({ features: response.data.results });
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};
